import type { ComponentChildren } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { X, RefreshCcw, ShieldCheck, Trash2, Loader2, PartyPopper, Star } from 'lucide-preact'
import { sendingDomainsApi } from '@/api/sendingDomains'
import { SENDING_DOMAIN_REGIONS, DEFAULT_SENDING_DOMAIN_REGION, type SendingDomainViewDto } from '@/types/sendingDomains'
import { useToast } from '@/hooks/useToast'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { useDomainVerification } from '@/hooks/useDomainVerification'
import { DnsRecordsTable } from './DnsRecordsTable'
import { DomainStatusBadge } from './DomainStatusBadge'
import { SendingDomainTrackingSection } from './SendingDomainTrackingSection'

// Domain is "actively verifying" (poll + show Checking…) only in these states — not NotStarted,
// where Resend hasn't been asked to check anything yet, and not the terminal states.
const VERIFYING_STATUSES = ['Pending', 'PartiallyVerified']

interface AddSendingDomainPanelProps {
  isOpen: boolean
  clientCompanyId: string
  /** Existing domain to manage, or null to add a new one. */
  domain: SendingDomainViewDto | null
  onClose: () => void
  onSaved: (domain: SendingDomainViewDto) => void
  onDeleted: (id: string) => void
}

/**
 * Full-screen overlay to add a sending domain (create) and then manage it (detail): shows the
 * DNS records to publish, polls verification status (only while actively verifying), and supports
 * Verify / Refresh / Remove. Full-bleed on every breakpoint with content capped to a readable width.
 */
export function AddSendingDomainPanel({ isOpen, clientCompanyId, domain, onClose, onSaved, onDeleted }: AddSendingDomainPanelProps) {
  const { showError, showSuccess } = useToast()
  const panelRef = useRef<HTMLDivElement>(null)

  const [domainInput, setDomainInput] = useState('')
  const [region, setRegion] = useState<string>(DEFAULT_SENDING_DOMAIN_REGION)
  const [creating, setCreating] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [settingDefault, setSettingDefault] = useState(false)
  // Domain shown in detail mode: the prop (manage) or the just-created one.
  const [activeDomain, setActiveDomain] = useState<SendingDomainViewDto | null>(domain)

  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    setDomainInput('')
    setRegion(DEFAULT_SENDING_DOMAIN_REGION)
    setActiveDomain(domain)
    setCreating(false)
    setVerifying(false)
    setDeleting(false)
    setSettingDefault(false)
  }, [isOpen, domain])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !creating && !verifying && !deleting && !settingDefault) onClose()
    }
    document.addEventListener('keydown', onKey)
    panelRef.current?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, creating, verifying, deleting, settingDefault, onClose])

  const inDetailMode = !!activeDomain
  // Only auto-poll once verification is actually underway (Verify clicked → Pending). Avoids the
  // misleading perpetual "Checking…" spinner on a freshly-added, Not-started domain.
  const isVerifying = !!activeDomain && VERIFYING_STATUSES.includes(activeDomain.Status)
  const verification = useDomainVerification(clientCompanyId, activeDomain, isOpen && isVerifying)
  const shownDomain = verification.status ?? activeDomain

  if (!isOpen) return null

  const isNotStarted = shownDomain?.Status === 'NotStarted'
  const isVerified = shownDomain?.Status === 'Verified'
  const regionLabel = shownDomain?.Region
    ? (SENDING_DOMAIN_REGIONS.find(r => r.value === shownDomain.Region)?.label ?? shownDomain.Region)
    : null

  const handleCreate = async () => {
    const value = domainInput.trim()
    if (!value) return
    setCreating(true)
    const res = await sendingDomainsApi.create(clientCompanyId, { Domain: value, Region: region })
    setCreating(false)
    if (res.error || !res.data) {
      showError('Could not add domain', res.error || 'Unknown error')
      return
    }
    showSuccess('Domain added', 'Add the DNS records below, then verify.')
    setActiveDomain(res.data)
    onSaved(res.data)
  }

  const handleVerify = async () => {
    if (!shownDomain) return
    setVerifying(true)
    const res = await sendingDomainsApi.verify(clientCompanyId, shownDomain.Id)
    setVerifying(false)
    if (res.error || !res.data) {
      showError('Verification failed to start', res.error || 'Unknown error')
      return
    }
    setActiveDomain(res.data)
    onSaved(res.data)
    showSuccess('Verification started', 'DNS checks can take a few minutes to hours.')
  }

  const handleSetDefault = async () => {
    if (!shownDomain) return
    setSettingDefault(true)
    const res = await sendingDomainsApi.setDefault(clientCompanyId, shownDomain.Id)
    setSettingDefault(false)
    if (res.error || !res.data) {
      showError('Could not set default', res.error || 'Unknown error')
      return
    }
    setActiveDomain(res.data)
    onSaved(res.data)
    showSuccess('Default updated', `${res.data.Domain} is now your default sending domain.`)
  }

  const handleDelete = async () => {
    if (!shownDomain) return
    setDeleting(true)
    const res = await sendingDomainsApi.remove(clientCompanyId, shownDomain.Id)
    setDeleting(false)
    if (res.error) {
      showError('Could not remove domain', res.error)
      return
    }
    onDeleted(shownDomain.Id)
    showSuccess('Domain removed', `${shownDomain.Domain} was removed.`)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-900" role="dialog" aria-modal="true">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white break-all">
              {inDetailMode ? shownDomain?.Domain : 'Add sending domain'}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {inDetailMode ? 'Publish the DNS records, then verify.' : 'Authenticate a subdomain you send marketing email from.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Body */}
      <div ref={panelRef} tabIndex={-1} className="flex-1 overflow-y-auto focus:outline-none">
        <div className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-6 space-y-6">
          {!inDetailMode && (
            <div className="max-w-xl space-y-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Domain</label>
              <input
                type="text"
                value={domainInput}
                onInput={e => setDomainInput((e.target as HTMLInputElement).value)}
                placeholder="marketing.yourbrand.com"
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Use a subdomain (e.g. <code className="font-mono">marketing.yourbrand.com</code>) — not your root domain.
              </p>

              <div className="pt-2 space-y-1">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Data residency region</label>
                <select
                  value={region}
                  onChange={e => setRegion((e.target as HTMLSelectElement).value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {SENDING_DOMAIN_REGIONS.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Where this domain's mail is processed. Fixed once created — to change it, remove and re-add.
                </p>
              </div>
            </div>
          )}

          {inDetailMode && shownDomain && (
            <>
              {isVerified ? (
                /* Verified: Resend-style header — message + metadata grid on a dotted green field */
                <div className="rounded-xl border border-green-200 dark:border-green-800/60 bg-green-50/70 dark:bg-green-900/15 p-4 sm:p-5 [background-image:radial-gradient(rgba(34,197,94,0.16)_1px,transparent_1px)] [background-size:14px_14px]">
                  <div className="flex items-center gap-2 text-sm font-medium text-green-800 dark:text-green-300">
                    <PartyPopper className="w-4 h-4 shrink-0" />
                    Domain verified — ready to send emails.
                  </div>
                  <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Meta label="Status"><DomainStatusBadge status={shownDomain.Status} /></Meta>
                    <Meta label="Region">{regionLabel ?? '—'}</Meta>
                    <Meta label="Added">{shownDomain.CreatedUtc ? new Date(shownDomain.CreatedUtc).toLocaleString() : '—'}</Meta>
                    <Meta label="Last checked">{shownDomain.LastCheckedUtc ? new Date(shownDomain.LastCheckedUtc).toLocaleString() : '—'}</Meta>
                  </dl>
                </div>
              ) : (
                /* Not yet verified: compact status row */
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <DomainStatusBadge status={shownDomain.Status} />
                  {verification.isPolling && (
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                      <Loader2 className="w-3 h-3 animate-spin" /> Checking…
                    </span>
                  )}
                  {regionLabel && <span className="text-xs text-slate-500 dark:text-slate-400">{regionLabel}</span>}
                  {shownDomain.CreatedUtc && (
                    <span className="text-xs text-slate-400">Added {new Date(shownDomain.CreatedUtc).toLocaleString()}</span>
                  )}
                  {shownDomain.LastCheckedUtc && (
                    <span className="text-xs text-slate-400">· Last checked {new Date(shownDomain.LastCheckedUtc).toLocaleTimeString()}</span>
                  )}
                </div>
              )}

              {shownDomain.StatusMessage && (
                <p className="text-xs text-amber-600 dark:text-amber-400">{shownDomain.StatusMessage}</p>
              )}
              {verification.error && (
                <p className="text-xs text-red-600 dark:text-red-400">{verification.error}</p>
              )}

              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">DNS records</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {isNotStarted
                    ? 'Add these to your DNS provider, then click Verify to start the DNS check.'
                    : 'Add these to your DNS provider. Verification runs automatically once they resolve.'}
                </p>
                <DnsRecordsTable records={shownDomain.DnsRecords} />
              </div>

              <SendingDomainTrackingSection
                clientCompanyId={clientCompanyId}
                domain={shownDomain}
                onUpdated={d => { setActiveDomain(d); onSaved(d) }}
              />
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="flex-shrink-0 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-4 flex items-center justify-between gap-2">
          {!inDetailMode ? (
            <>
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating || !domainInput.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {creating && <Loader2 className="w-4 h-4 animate-spin" />} Add domain
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Remove
              </button>
              <div className="flex items-center gap-2">
                {isVerified && (shownDomain.IsDefault ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium">
                    <Star className="w-4 h-4 fill-current" /> Default
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleSetDefault}
                    disabled={settingDefault}
                    title="Use this domain by default on new activations and test sends"
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 disabled:opacity-50"
                  >
                    {settingDefault ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />} Set as default
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => { void verification.refresh() }}
                  disabled={verification.isLoading || isNotStarted}
                  title={isNotStarted ? 'Click Verify first to start the DNS check' : 'Re-check status'}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCcw className={`w-4 h-4 ${verification.isLoading ? 'animate-spin' : ''}`} /> Refresh
                </button>
                <button
                  type="button"
                  onClick={handleVerify}
                  disabled={verifying || isVerified}
                  title={isVerified ? 'Domain is already verified' : undefined}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Verify
                </button>
              </div>
            </>
          )}
        </div>
      </footer>
    </div>
  )
}

/** A labeled metadata cell for the verified-domain header grid. */
function Meta({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-green-700/70 dark:text-green-300/60">{label}</div>
      <div className="mt-1 text-sm text-slate-800 dark:text-slate-100">{children}</div>
    </div>
  )
}
